import { Link } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner, EmptyState, Badge } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';

export function HistoryPage() {
  const projectsQuery = useQuery({ queryKey: ['projects'], queryFn: () => api.listProjects() });
  const projects = projectsQuery.data?.projects ?? [];

  const runsQueries = useQueries({
    queries: projects.map((p) => ({
      queryKey: ['project-runs', p.id],
      queryFn: () => api.listProjectRuns(p.id),
    })),
  });

  const loading = projectsQuery.isLoading || runsQueries.some((q) => q.isLoading);

  const rows = projects
    .flatMap((p, i) =>
      (runsQueries[i].data?.runs ?? []).map((run) => ({ project: p, run })),
    )
    .sort((a, b) => (a.run.createdAt < b.run.createdAt ? 1 : -1));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-primary">History</h1>
        <p className="mt-1 text-sm text-text-secondary">All analyses across your projects.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner className="h-7 w-7" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<History size={28} />}
          title="No analysis history yet"
          description="Run an analysis to start building your history."
        />
      ) : (
        <Card>
          <CardContent className="px-0 pt-0">
            <ul className="divide-y divide-border/60">
              {rows.map(({ project, run }) => (
                <li key={run.runId}>
                  <Link
                    to={`/app/results/${run.runId}`}
                    className="flex items-center justify-between px-5 py-4 transition hover:bg-bg-raised/40"
                  >
                    <div>
                      <p className="text-sm font-medium text-text-primary">{project.name}</p>
                      <p className="text-xs text-text-muted">{formatDate(run.createdAt)}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-text-secondary">
                        Visibility{' '}
                        <span className="font-semibold text-text-primary">
                          {run.metrics?.visibilityScore.toFixed(1) ?? '—'}
                        </span>
                      </span>
                      <Badge tone={run.status === 'completed' ? 'teal' : 'blue'}>{run.status}</Badge>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
